import { Box, Stack, Title } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { useMst } from '#state'
import { IOrgOptionModel, WorkspaceOptionValues } from '#state/types'

import { NoPermissions } from './NoPermissions'
import { PermissionAccordions } from './PermissionAccordions'
import {
  CHANGE_SCRIPT_STATUS_CONFIG,
  DESTROY_CONTENT_CONFIG,
  ENABLE_CHANGE_TRACKING_CONFIG,
  GROUPED_PERMISSIONS,
  MANAGE_REVISIONS_CONFIG,
  MODIFY_LIMITED_SCRIPT_CONFIG,
  PAGE_LOCKER_CONFIG,
  PermissionConfig,
  PUSH_TO_PROMPTER_CONFIG,
  REVIEW_TRACKED_CHANGES_CONFIG,
  UPDATE_RUNDOWNS_CONFIG,
} from './util'

const { PAGE_LOCKING, PROMPTER_INTEGRATION, RUNDOWNS, SCRIPT_LIMITING } =
  WorkspaceOptionValues

const permissionsToInclude = (
  options: IOrgOptionModel[],
  includeChangeTracking: boolean,
  includeRevisions: boolean,
) => {
  const permissionsToInclude: PermissionConfig[] = []

  if (includeChangeTracking) {
    permissionsToInclude.push({
      ...ENABLE_CHANGE_TRACKING_CONFIG,
      enabled: true,
    })
    permissionsToInclude.push({
      ...REVIEW_TRACKED_CHANGES_CONFIG,
      enabled: true,
    })
  }

  if (includeRevisions) {
    permissionsToInclude.push({
      ...MANAGE_REVISIONS_CONFIG,
      enabled: true,
    })
  }

  const pageLocking = options.find(
    (o: IOrgOptionModel) => o.code === PAGE_LOCKING,
  )

  // when page-locking is generally available, we can stop including it conditionally
  if (pageLocking?.enabled) {
    permissionsToInclude.push({ ...PAGE_LOCKER_CONFIG, enabled: true })
  }

  const scriptLimiting = options.find(
    (o: IOrgOptionModel) => o.code === SCRIPT_LIMITING,
  )

  permissionsToInclude.push(
    { ...CHANGE_SCRIPT_STATUS_CONFIG, enabled: !!scriptLimiting?.enabled },
    { ...MODIFY_LIMITED_SCRIPT_CONFIG, enabled: !!scriptLimiting?.enabled },
  )

  const prompterIntegration = options.find(
    (o: IOrgOptionModel) => o.code === PROMPTER_INTEGRATION,
  )

  permissionsToInclude.push({
    ...PUSH_TO_PROMPTER_CONFIG,
    enabled: !!prompterIntegration?.enabled,
  })

  const rundowns = options.find((o: IOrgOptionModel) => o.code === RUNDOWNS)

  permissionsToInclude.push({
    ...UPDATE_RUNDOWNS_CONFIG,
    enabled: !!rundowns?.enabled,
  })

  permissionsToInclude.push({
    ...DESTROY_CONTENT_CONFIG,
    enabled: true,
  })

  return permissionsToInclude
}

export const PermissionSettings = observer(function PermissionSettings() {
  const { currentOrg, view } = useMst()

  if (!currentOrg) return <NoPermissions />

  const permissions = permissionsToInclude(
    currentOrg.options,
    currentOrg.hasChangeTrackingEnabled,
    view.revisionsEnabled,
  )

  const permissionsByGroup = Object.fromEntries(
    Object.entries(GROUPED_PERMISSIONS).map(([groupKey, groupPermissions]) => [
      groupKey,
      groupPermissions
        .map((p) => permissions.find((p2) => p2.code === p))
        .filter((p) => p !== undefined),
    ]),
  )

  return (
    <Box px={30}>
      <h1>Permissions</h1>
      <Stack gap="lg">
        {Object.entries(permissionsByGroup).map(([group, groupPermissions]) => (
          <div key={group}>
            <Title order={2} size={16} fw={700} mb={12} pl={1}>
              {group}
            </Title>
            <PermissionAccordions
              permissions={groupPermissions}
              org={currentOrg}
            />
          </div>
        ))}
      </Stack>
    </Box>
  )
})
